'use client';

/**
 * The consent surface mounted by the customer layouts (GitLab #649).
 *
 * One component so a layout wires consent in a single line and cannot mount
 * half of it: the provider that owns the state, the gate that asks, and the
 * GTM loader that acts on the answer. The footer's "Cookie settings" link
 * reads the same context, which is why `children` sit inside the provider.
 *
 * Suppression is decided here rather than by the layout because it depends
 * on the current path, and a layout does not re-render on client navigation
 * within its own subtree. `usePathname` does, so a customer who moves onto a
 * hand-off route is not asked mid-journey (§4.4).
 */
import { c as _c } from "react/compiler-runtime";
import { useState, type ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import { ConsentProvider } from '@/components/analytics/ConsentProvider';
import { CookieConsentGate } from '@/components/analytics/CookieConsentGate';
import { GtmLoader } from '@/components/analytics/GtmLoader';
import { SentryReplayConsent } from '@/components/analytics/SentryReplayConsent';
import { hasConsentGateSuppressedQuery, isConsentGateSuppressed } from '@/lib/analytics/consentRoutes';
import { recordConsentChoice } from '@/lib/analytics/recordConsent';
import type { AnalyticsSettings, LegalContentSettings } from '@/types/organization';
export interface ConsentSurfaceProps {
  /** Resolved settings for the child org being browsed. */
  settings: AnalyticsSettings;
  /** Child org the choice is given under; recorded on the evidence row. */
  organizationId?: string;
  /** Per-request CSP nonce, stamped onto the GTM container script. */
  nonce?: string;
  /** Explicit suppression for flows that should never prompt in this view. */
  suppressGate?: boolean;
  /** The org's authored legal content, so the gate can show its cookie policy. */
  legalContent?: LegalContentSettings;
  children: ReactNode;
}
export function ConsentSurface(t0) {
  const $ = _c(18);
  const {
    settings,
    organizationId,
    nonce,
    suppressGate: suppressGateOverride,
    legalContent,
    children
  } = t0;
  const pathname = usePathname();
  const [suppressedByQuery] = useState(_temp);
  let t1;
  if ($[0] !== pathname || $[1] !== suppressGateOverride || $[2] !== suppressedByQuery) {
    t1 = suppressGateOverride || suppressedByQuery || isConsentGateSuppressed(pathname ?? "/");
    $[0] = pathname;
    $[1] = suppressGateOverride;
    $[2] = suppressedByQuery;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const suppressGate = t1;
  let t2;
  if ($[4] !== nonce || $[5] !== settings.gtm_container_id) {
    t2 = <GtmLoader containerId={settings.gtm_container_id} nonce={nonce} />;
    $[4] = nonce;
    $[5] = settings.gtm_container_id;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <SentryReplayConsent />;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== legalContent || $[9] !== settings) {
    t4 = <CookieConsentGate settings={settings} legalContent={legalContent} />;
    $[8] = legalContent;
    $[9] = settings;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== children || $[12] !== organizationId || $[13] !== settings || $[14] !== suppressGate || $[15] !== t2 || $[16] !== t4) {
    t5 = <ConsentProvider settings={settings} organizationId={organizationId} suppressGate={suppressGate} onChoice={recordConsentChoice}>{children}{t2}{t3}{t4}</ConsentProvider>;
    $[11] = children;
    $[12] = organizationId;
    $[13] = settings;
    $[14] = suppressGate;
    $[15] = t2;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
}
function _temp() {
  return typeof window !== "undefined" && hasConsentGateSuppressedQuery(window.location.search);
}